/**
 * The three-tier filter over a list of assets: asset kind, then the element's
 * type, then its subtype. The plant agent review queue and the workspace asset
 * rail filter the same facets, so the rule lives once. The asset rail's tab is
 * its kind tier.
 *
 * A tier opens only after the one above it holds a value, and changing a tier
 * clears the tiers below it, so the options a select offers are always those
 * of the items the tiers above still admit.
 */
import type { MarkingKind } from '../types';

export const ASSET_KINDS: MarkingKind[] = ['instrument', 'node', 'equipment'];

export const ASSET_KIND_LABELS: Record<MarkingKind, string> = {
  instrument: 'Instrument',
  node: 'Line',
  equipment: 'Equipment',
};

/** What one pending element is filtered by. */
export interface AssetFacets {
  kind: MarkingKind;
  element_type: string | null;
  subtype: string | null;
}

/** One value per tier; '' is "not chosen", which admits every item at that tier. */
export interface AssetFilter {
  kind: MarkingKind | '';
  elementType: string;
  subtype: string;
}

export type AssetFilterTier = keyof AssetFilter;

export const NO_ASSET_FILTER: AssetFilter = { kind: '', elementType: '', subtype: '' };

const TIERS: AssetFilterTier[] = ['kind', 'elementType', 'subtype'];

/** The filter with one tier set and every tier below it cleared. */
export function withAssetFilterTier(
  filter: AssetFilter,
  tier: AssetFilterTier,
  value: string,
): AssetFilter {
  const next = { ...filter, [tier]: value } as AssetFilter;
  for (const below of TIERS.slice(TIERS.indexOf(tier) + 1)) next[below] = '';
  return next;
}

/** Only an instrument's filter has a subtype tier; equipment and lines stop at the type tier. */
export function assetHasSubtypeTier(kind: MarkingKind | ''): boolean {
  return kind === 'instrument';
}

export function assetMatchesFilter(facets: AssetFacets, filter: AssetFilter): boolean {
  if (filter.kind && facets.kind !== filter.kind) return false;
  if (filter.elementType && (facets.element_type ?? '') !== filter.elementType) return false;
  if (filter.subtype && (facets.subtype ?? '') !== filter.subtype) return false;
  return true;
}

export interface AssetFilterOptions {
  kinds: MarkingKind[];
  elementTypes: string[];
  subtypes: string[];
}

function distinctSorted(values: Array<string | null>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) => a.localeCompare(b));
}

/** What each select offers: the values the items admitted by the tiers above it carry. */
export function assetFilterOptions(items: AssetFacets[], filter: AssetFilter): AssetFilterOptions {
  const ofKind = filter.kind ? items.filter((item) => item.kind === filter.kind) : [];
  const ofType = filter.elementType ? ofKind.filter((item) => item.element_type === filter.elementType) : [];
  return {
    kinds: ASSET_KINDS.filter((kind) => items.some((item) => item.kind === kind)),
    elementTypes: distinctSorted(ofKind.map((item) => item.element_type)),
    subtypes: assetHasSubtypeTier(filter.kind) ? distinctSorted(ofType.map((item) => item.subtype)) : [],
  };
}

/**
 * The filter after the items changed under it. A tier whose value no item
 * offers any more is cleared, with the tiers below it, so the select never
 * shows "All" while a vanished value still hides every row. A refetch after a
 * review is the usual cause: the last `OPC In` leaves, and the filter follows.
 */
export function reconcileAssetFilter(filter: AssetFilter, items: AssetFacets[]): AssetFilter {
  const next = filter;
  for (const tier of TIERS) {
    const value = next[tier];
    if (!value) break;
    const options = assetFilterOptions(items, next);
    const offered: string[] =
      tier === 'kind' ? options.kinds : tier === 'elementType' ? options.elementTypes : options.subtypes;
    if (!offered.includes(value)) return withAssetFilterTier(next, tier, '');
  }
  return next;
}
